/**
 * 掲示板（BBS）— BBS_SPEC §4 画面仕様
 *
 * 共通掲示板1つ＋各街区に1つ（決定22）。擬似匿名＝handle固定（決定23）。
 * ★表示は API が返す handle のみ。実アカウント名・author_id は API 側で返らない（BBS_SPEC §3）。
 * 一覧は新着更新順のみ（人気順ランキングは出さない・§9）。数値は実データのみ（§7 事実性）。
 * 4状態（empty/loading/error/partial）・44px・reduced-motion・YES/NO禁止（§8・§7）。
 */
import { FC, ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { api, BoardSpace, BoardThread, BoardReply, BoardAttach, Post, SalonNote, StageScript } from '../services/api';
import { PulseButton } from '../components/ui/PulseButton';
import { Icon } from '../components/icons/Icon';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { TownShell, TownLoading, TownError, TownEmpty, useTownUser, timeAgo, TownRole } from '../components/town/townShared';

type Load = 'loading' | 'ready' | 'error';

/** 掲示板の設置場所ごとの見え方（役割色は識別専用・3役対等） */
const SPACE_META: Record<BoardSpace, { title: string; kicker: string; role: TownRole; backTo?: string; backLabel?: string }> = {
    common: { title: '三役の広場 掲示板', kicker: 'TOWN BOARD', role: 'script' },
    salon: { title: '文壇サロン 掲示板', kicker: "WRITERS' BOARD", role: 'script', backTo: '/salon', backLabel: '文壇サロンへ' },
    tokiwa: { title: 'トキワ荘 掲示板', kicker: "ATELIER BOARD", role: 'illustration', backTo: '/tokiwa', backLabel: 'トキワ荘へ' },
    stage: { title: '声劇ステージ 掲示板', kicker: 'STAGE BOARD', role: 'voice', backTo: '/stage', backLabel: '声劇ステージへ' },
};

const ATTACH_LABEL: Record<BoardAttach['type'], string> = { post: '作品', note: 'ノート', script: '台本' };

// ══════════════════════════════════════════════════════════
// 掲示板トップ
// ══════════════════════════════════════════════════════════
const BoardTop: FC<{ space: BoardSpace }> = ({ space }) => {
    const [, setLocation] = useLocation();
    const { user, requireLogin, loginGate } = useTownUser();
    const { playSound } = useGamenics();
    const [threads, setThreads] = useState<BoardThread[]>([]);
    const [state, setState] = useState<Load>('loading');
    const [composing, setComposing] = useState(false);
    const [handleModal, setHandleModal] = useState(false);
    const meta = SPACE_META[space];

    const load = () => {
        setState('loading');
        api.boardListThreads(space)
            .then(rows => { setThreads(rows); setState('ready'); })
            .catch(() => setState('error'));
    };
    useEffect(load, [space]);

    return (
        <TownShell
            role={meta.role}
            kicker={meta.kicker}
            title={meta.title}
            actions={
                meta.backTo && (
                    <button className="btn btn-ghost btn-sm min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(meta.backTo!); }}>
                        {meta.backLabel}
                    </button>
                )
            }
        >
            <p className="text-ink-2 text-sm mb-4 max-w-2xl">
                作品がなくても、ここでは話せます。掲示板での名前（ハンドル）で書き込むので、アカウント名は出ません。
            </p>

            {/* 共通⇄街区の切替（意思決定アクションを増やしすぎない範囲で） */}
            <nav aria-label="掲示板の切替" className="flex flex-wrap gap-2 mb-6">
                {(Object.keys(SPACE_META) as BoardSpace[]).map(s => (
                    <button
                        key={s}
                        onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(s === 'common' ? '/board' : `/${s}/board`); }}
                        aria-current={s === space ? 'page' : undefined}
                        className={`min-h-[44px] px-3 py-1.5 rounded-pop-sm border-2 text-[13px] font-bold ${s === space ? 'bg-ink text-white border-ink' : 'bg-surface text-ink-2 border-ink/20 hover:-translate-y-0.5 transition-transform'}`}
                    >
                        {s === 'common' ? '広場' : s === 'salon' ? 'サロン' : s === 'tokiwa' ? 'トキワ荘' : 'ステージ'}
                    </button>
                ))}
            </nav>

            {/* 主CTAは1つ（パルスもここだけ） */}
            <div className="mb-6">
                <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => setComposing(true)); }}>
                    スレッドを立てる
                </PulseButton>
            </div>

            {state === 'loading' && <TownLoading />}
            {state === 'error' && <TownError message="掲示板を読み込めませんでした" onRetry={load} />}
            {state === 'ready' && threads.length === 0 && (
                <TownEmpty title="まだ話は始まっていません。最初のひとことを。">
                    <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => setComposing(true)); }}>
                        スレッドを立てる
                    </PulseButton>
                </TownEmpty>
            )}
            {state === 'ready' && threads.length > 0 && (
                <ul className="flex flex-col gap-3 list-none p-0 m-0">
                    {threads.map(t => (
                        <li key={t.id}>
                            <button
                                onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(`/board/threads/${t.id}`); }}
                                className="pop-card w-full text-left p-4 min-h-[44px] hover:-translate-y-0.5 transition-transform"
                            >
                                <div className="flex items-start gap-2">
                                    <h2 className="font-display font-bold text-ink text-base flex-1">{t.title}</h2>
                                    {t.attach_ref && (
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-cream-2 text-ink-2 border border-ink/20 flex-shrink-0">
                                            {ATTACH_LABEL[t.attach_ref.type]}を添付
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-ink-2 mt-1 line-clamp-2 whitespace-pre-wrap">{t.body}</p>
                                {/* 数値は実データのみ。「今〇人が見ています」等は出さない（§7 事実性） */}
                                <div className="flex items-center gap-3 mt-2 text-[11px] text-ink-3">
                                    <span className="font-bold text-ink-2">{t.handle}</span>
                                    <span className="tabular-nums">返信 {t.reply_count}</span>
                                    {t.reaction_count > 0 && <span className="tabular-nums">反応 {t.reaction_count}</span>}
                                    <span className="tabular-nums ml-auto">{timeAgo(t.last_reply_at ?? t.created_at)}</span>
                                </div>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {composing && (
                <ThreadComposer
                    space={space}
                    onNeedHandle={() => { setComposing(false); setHandleModal(true); }}
                    onClose={() => setComposing(false)}
                    onSaved={(id) => { setComposing(false); if (id) setLocation(`/board/threads/${id}`); else load(); }}
                />
            )}
            {handleModal && (
                <HandleModal onClose={() => setHandleModal(false)} onSaved={() => { setHandleModal(false); setComposing(true); }} />
            )}
            {loginGate}
            {user && null}
        </TownShell>
    );
};

export const BoardCommon: FC = () => <BoardTop space="common" />;
export const BoardSalon: FC = () => <BoardTop space="salon" />;
export const BoardTokiwa: FC = () => <BoardTop space="tokiwa" />;
export const BoardStage: FC = () => <BoardTop space="stage" />;

// ══════════════════════════════════════════════════════════
// スレッド詳細 /board/threads/:id
// ══════════════════════════════════════════════════════════
export const BoardThreadDetail = ({ params }: any) => {
    const id: string = params?.id;
    const [, setLocation] = useLocation();
    const { user, requireLogin, loginGate } = useTownUser();
    const { playSound } = useGamenics();
    const [thread, setThread] = useState<BoardThread | null>(null);
    const [replies, setReplies] = useState<BoardReply[]>([]);
    const [state, setState] = useState<Load>('loading');
    const [replyText, setReplyText] = useState('');
    const [replyAttach, setReplyAttach] = useState<BoardAttach | null>(null);
    const [sending, setSending] = useState(false);
    const [handleModal, setHandleModal] = useState(false);
    const [myHandle, setMyHandle] = useState<string | null>(null);

    const load = () => {
        setState('loading');
        api.boardGetThread(id)
            .then(d => {
                if (!d?.thread) { setState('error'); return; }
                setThread(d.thread); setReplies(d.replies ?? []); setState('ready');
            })
            .catch(() => setState('error'));
    };
    useEffect(load, [id]);
    useEffect(() => { if (user) api.boardGetHandle().then(setMyHandle); }, [user]);

    // 「自分の投稿」は handle 一致で判定（API は author_id を返さないため）
    const isMine = (handle: string) => !!myHandle && handle === myHandle;

    const submitReply = () => requireLogin(async () => {
        if (!replyText.trim()) return;
        setSending(true);
        const r = await api.boardCreateReply(id, { body: replyText.trim(), attach_ref: replyAttach });
        setSending(false);
        if (r.code === 'HANDLE_REQUIRED') { setHandleModal(true); return; }
        if (!r.success) { alert(r.error || 'ひとこと返せませんでした'); return; }
        playSound(GamenicsEvent.SUBMISSION_COMPLETE);
        setReplyText(''); setReplyAttach(null);
        load();
    });

    const react = (targetType: 'thread' | 'reply', targetId: string) => requireLogin(async () => {
        const r = await api.boardToggleReaction(targetType, targetId);
        if (r.success) { playSound(GamenicsEvent.TAP_CONFIRM); load(); }
    });

    const meta = thread ? SPACE_META[thread.space] : SPACE_META.common;

    return (
        <TownShell role={meta.role} kicker="THREAD" title="スレッド">
            {state === 'loading' && <TownLoading />}
            {state === 'error' && <TownError message="スレッドを読み込めませんでした" onRetry={load} />}
            {state === 'ready' && thread && (
                <div className="max-w-[760px]">
                    {/* スレッド本体 */}
                    <article className="pop-card p-5 mb-4">
                        <h1 className="font-display font-bold text-ink text-lg mb-2">{thread.title}</h1>
                        <div className="flex items-center gap-3 text-[11px] text-ink-3 mb-3">
                            <span className="font-bold text-ink-2">{thread.handle}</span>
                            <span className="tabular-nums">{timeAgo(thread.created_at)}</span>
                        </div>
                        {/* 本文はプレーンテキスト（改行のみ保持・HTMLは解釈しない） */}
                        <p className="text-ink whitespace-pre-wrap leading-relaxed text-[15px]">{thread.body}</p>
                        {thread.attach_ref && <AttachCard attach={thread.attach_ref} />}
                        <div className="flex items-center gap-2 mt-4">
                            <ReactionButton count={thread.reaction_count} onClick={() => react('thread', thread.id)} />
                            {isMine(thread.handle) && (
                                <button
                                    className="btn btn-ghost btn-sm min-h-[44px]"
                                    onClick={() => {
                                        if (confirm('このスレッドを手放しますか？')) {
                                            api.boardDeleteThread(thread.id).then(r => { if (r.success) setLocation(thread.space === 'common' ? '/board' : `/${thread.space}/board`); });
                                        }
                                    }}
                                >
                                    手放す
                                </button>
                            )}
                            <button className="btn btn-ghost btn-sm min-h-[44px] ml-auto" onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); setLocation(thread.space === 'common' ? '/board' : `/${thread.space}/board`); }}>
                                ちょっと戻る
                            </button>
                        </div>
                    </article>

                    {/* 返信（時系列） */}
                    <h2 className="text-xs font-bold text-ink-2 mb-2 tracking-wide">返信 <span className="tabular-nums">{replies.length}</span></h2>
                    {replies.length === 0 ? (
                        <p className="text-sm text-ink-3 mb-4">まだ返信はありません。最初のひとことを。</p>
                    ) : (
                        <ul className="flex flex-col gap-2 list-none p-0 m-0 mb-4">
                            {replies.map((r, i) => (
                                <li key={r.id} className="pop-card p-4">
                                    <div className="flex items-center gap-2 text-[11px] text-ink-3 mb-1">
                                        <span className="text-ink-2 font-mono">{`>>${i + 1}`}</span>
                                        <span className="font-bold text-ink-2">{r.handle}</span>
                                        <span className="tabular-nums">{timeAgo(r.created_at)}</span>
                                    </div>
                                    <p className="text-ink whitespace-pre-wrap text-sm leading-relaxed">{r.body}</p>
                                    {r.attach_ref && <AttachCard attach={r.attach_ref} />}
                                    <div className="flex items-center gap-2 mt-2">
                                        <ReactionButton count={r.reaction_count} onClick={() => react('reply', r.id)} />
                                        {isMine(r.handle) && (
                                            <button
                                                className="text-[11px] text-ink-3 underline min-h-[44px]"
                                                onClick={() => { if (confirm('この返信を手放しますか？')) api.boardDeleteReply(r.id).then(res => { if (res.success) load(); }); }}
                                            >
                                                手放す
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* 返信フォーム（主CTA・パルスは1画面1箇所＝ここ） */}
                    <div className="pop-card p-4">
                        <label className="block text-xs font-bold text-ink-2 mb-2" htmlFor="reply-body">ひとこと返す</label>
                        <textarea
                            id="reply-body"
                            value={replyText}
                            onChange={e => setReplyText(e.target.value)}
                            rows={3}
                            maxLength={1000}
                            className="pop-field w-full resize-none mb-2"
                            placeholder="思ったことを、そのまま。"
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                            <AttachPicker value={replyAttach} onChange={setReplyAttach} />
                            <span className="text-[11px] text-ink-3 tabular-nums ml-auto">{replyText.length}/1000</span>
                        </div>
                        <div className="mt-3">
                            <PulseButton isPrimary onClick={submitReply} disabled={sending || !replyText.trim()}>
                                {sending ? '返しています…' : 'ひとこと返す'}
                            </PulseButton>
                        </div>
                    </div>
                </div>
            )}

            {handleModal && (
                <HandleModal onClose={() => setHandleModal(false)} onSaved={() => { setHandleModal(false); api.boardGetHandle().then(setMyHandle); }} />
            )}
            {loginGate}
        </TownShell>
    );
};

// ══════════════════════════════════════════════════════════
// 部品
// ══════════════════════════════════════════════════════════

const ReactionButton: FC<{ count: number; onClick: () => void }> = ({ count, onClick }) => (
    <button
        onClick={onClick}
        className="min-h-[44px] px-3 py-1.5 rounded-full border-2 border-ink/20 bg-surface text-ink-2 text-[12px] font-bold hover:bg-cream-2 transition-colors inline-flex items-center gap-1.5"
        aria-label="この投稿に反応する"
    >
        <Icon name="heart" size={14} />
        {/* 実数のみ。0なら数値を出さない（偽の賑わいを作らない） */}
        {count > 0 && <span className="tabular-nums">{count}</span>}
    </button>
);

/** 引用添付カード（BBS-03）。参照先が取れないときは架空値で埋めない（partial・§4.2） */
const AttachCard: FC<{ attach: BoardAttach }> = ({ attach }) => {
    const [, setLocation] = useLocation();
    const [title, setTitle] = useState<string | null | undefined>(undefined); // undefined=解決中, null=取得不能
    const { playSound } = useGamenics();

    useEffect(() => {
        let cancelled = false;
        const resolve = async () => {
            try {
                if (attach.type === 'post') {
                    const d = await api.getPostDetailById(attach.id);
                    if (!cancelled) setTitle(d?.post?.title ?? null);
                } else if (attach.type === 'note') {
                    const n = await api.salonGetNote(attach.id);
                    if (!cancelled) setTitle(n?.title ?? null);
                } else {
                    const s = await api.stageGetScript(attach.id);
                    if (!cancelled) setTitle(s?.title ?? null);
                }
            } catch { if (!cancelled) setTitle(null); }
        };
        resolve();
        return () => { cancelled = true; };
    }, [attach.type, attach.id]);

    const href = attach.type === 'post' ? `/post/${attach.id}`
        : attach.type === 'note' ? `/salon/notes/${attach.id}`
            : `/stage/scripts/${attach.id}`;

    return (
        <div className="mt-3 rounded-pop-sm border-2 border-ink/20 bg-cream p-3">
            <span className="text-[10px] font-bold text-ink-3 uppercase tracking-wide">{ATTACH_LABEL[attach.type]}</span>
            {title === undefined ? (
                <div className="h-4 w-40 bg-cream-2 rounded mt-1 motion-safe:animate-pulse" aria-label="参照先を読み込み中" />
            ) : title === null ? (
                <p className="text-sm text-ink-3 mt-0.5">参照先を読み込めません</p>
            ) : (
                <button
                    onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(href); }}
                    className="block text-left text-sm font-bold text-ink underline mt-0.5 min-h-[44px]"
                >
                    {title}
                </button>
            )}
        </div>
    );
};

/** 引用添付の選択（BBS-03）。自分の作品/ノート/台本から1件 */
const AttachPicker: FC<{ value: BoardAttach | null; onChange: (a: BoardAttach | null) => void }> = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<BoardAttach['type']>('post');
    const [items, setItems] = useState<{ id: string; label: string }[] | null>(null);
    const { playSound } = useGamenics();

    const loadItems = async (t: BoardAttach['type']) => {
        setItems(null);
        try {
            if (t === 'post') {
                const posts: Post[] = await api.getUserSubmissions();
                setItems(posts.map((p: any) => ({ id: p.id, label: p.title || '無題の作品' })));
            } else if (t === 'note') {
                const notes: SalonNote[] = await api.salonListNotes();
                setItems(notes.map(n => ({ id: n.id, label: n.title || '無題のノート' })));
            } else {
                const scripts: StageScript[] = await api.stageListScripts();
                setItems(scripts.map(s => ({ id: s.id, label: s.title })));
            }
        } catch { setItems([]); }
    };

    if (value) {
        return (
            <span className="inline-flex items-center gap-2 text-[12px] font-bold text-ink-2 bg-cream-2 border border-ink/20 rounded-full px-3 py-1.5">
                {ATTACH_LABEL[value.type]}を添付中
                <button onClick={() => onChange(null)} className="text-ink-3 min-h-[32px]" aria-label="添付をはずす"><Icon name="x" size={12} /></button>
            </span>
        );
    }

    return (
        <>
            <button
                className="btn btn-ghost btn-sm min-h-[44px]"
                onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setOpen(true); loadItems(tab); }}
            >
                <Icon name="plus" size={14} /> 作品を添える
            </button>
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
                    <div className="relative bg-surface border-2 border-ink rounded-pop p-5 w-full max-w-md max-h-[80vh] overflow-y-auto">
                        <h2 className="font-display font-bold text-lg mb-3">添えるものを選ぶ</h2>
                        {/* 3役対等: 作品・ノート・台本を同じ重みで並べる */}
                        <div className="flex gap-2 mb-3">
                            {(['post', 'note', 'script'] as const).map(t => (
                                <button
                                    key={t}
                                    onClick={() => { setTab(t); loadItems(t); }}
                                    aria-pressed={tab === t}
                                    className={`btn btn-sm flex-1 min-h-[44px] ${tab === t ? 'btn-primary' : 'btn-ghost'}`}
                                >
                                    {ATTACH_LABEL[t]}
                                </button>
                            ))}
                        </div>
                        {items === null ? (
                            <TownLoading label="読み込んでいます" />
                        ) : items.length === 0 ? (
                            <p className="text-sm text-ink-3 py-4">添えられるものがまだありません。</p>
                        ) : (
                            <ul className="flex flex-col gap-2 list-none p-0 m-0">
                                {items.map(it => (
                                    <li key={it.id}>
                                        <button
                                            onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); onChange({ type: tab, id: it.id }); setOpen(false); }}
                                            className="w-full text-left px-3 py-2 min-h-[44px] rounded-pop-sm border-2 border-ink bg-cream hover:bg-sun text-sm font-bold text-ink"
                                        >
                                            {it.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <button className="btn btn-ghost w-full min-h-[44px] mt-3" onClick={() => setOpen(false)}>やっぱりやめる</button>
                    </div>
                </div>
            )}
        </>
    );
};

/** スレッド作成 */
const ThreadComposer: FC<{ space: BoardSpace; onNeedHandle: () => void; onClose: () => void; onSaved: (id?: string) => void }> = ({ space, onNeedHandle, onClose, onSaved }) => {
    const { playSound } = useGamenics();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [attach, setAttach] = useState<BoardAttach | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const save = async () => {
        if (!title.trim() || !body.trim()) { setError('タイトルと本文を入れてください'); return; }
        setSaving(true); setError(null);
        const r = await api.boardCreateThread({ space, title: title.trim(), body: body.trim(), attach_ref: attach });
        setSaving(false);
        // BBS-05: ハンドル未設定なら設定モーダルへ
        if (r.code === 'HANDLE_REQUIRED') { onNeedHandle(); return; }
        if (!r.success) { setError(r.error || 'スレッドを立てられませんでした'); return; }
        playSound(GamenicsEvent.SUBMISSION_COMPLETE);
        onSaved(r.id);
    };

    return (
        <ModalShell onClose={onClose} title="スレッドを立てる">
            <label className="block text-sm text-ink-2 mb-1" htmlFor="th-title">タイトル</label>
            <input id="th-title" value={title} onChange={e => setTitle(e.target.value)} maxLength={80} className="pop-field w-full mb-1" placeholder="何について話したい？" />
            <div className="text-[11px] text-ink-3 text-right tabular-nums mb-3">{title.length}/80</div>

            <label className="block text-sm text-ink-2 mb-1" htmlFor="th-body">本文</label>
            <textarea id="th-body" value={body} onChange={e => setBody(e.target.value)} rows={6} maxLength={2000} className="pop-field w-full resize-none mb-1" placeholder="話しかけてみましょう。" />
            <div className="text-[11px] text-ink-3 text-right tabular-nums mb-3">{body.length}/2000</div>

            <div className="mb-3"><AttachPicker value={attach} onChange={setAttach} /></div>
            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

            <div className="flex gap-3">
                <button className="btn btn-ghost flex-1 min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}>やっぱりやめる</button>
                <PulseButton isPrimary className="flex-1" onClick={save} disabled={saving}>
                    {saving ? '立てています…' : '話しかける'}
                </PulseButton>
            </div>
        </ModalShell>
    );
};

/** ハンドル設定モーダル（BBS-05・決定23） */
const HandleModal: FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
    const { playSound } = useGamenics();
    const [handle, setHandle] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => { api.boardGetHandle().then(h => { if (h) setHandle(h); }); }, []);

    const save = async () => {
        if (!handle.trim()) { setError('名前を入れてください'); return; }
        setSaving(true); setError(null);
        const r = await api.boardSetHandle(handle.trim());
        setSaving(false);
        if (!r.success) { setError(r.error || 'この名前は使えませんでした'); return; }
        playSound(GamenicsEvent.TAP_CONFIRM);
        onSaved();
    };

    return (
        <ModalShell onClose={onClose} title="掲示板での名前を決める">
            <p className="text-sm text-ink-2 mb-3">
                掲示板ではこの名前で表示されます。<strong className="text-ink">アカウント名は出ません。</strong><br />
                あとから変えられます（変えると過去の書き込みの表示も新しい名前になります）。
            </p>
            <label className="block text-sm text-ink-2 mb-1" htmlFor="bbs-handle">名前</label>
            <input id="bbs-handle" value={handle} onChange={e => setHandle(e.target.value)} maxLength={20} className="pop-field w-full mb-1" placeholder="掲示板での呼ばれ方" autoFocus />
            <div className="text-[11px] text-ink-3 text-right tabular-nums mb-3">{handle.length}/20</div>
            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
            <div className="flex gap-3">
                <button className="btn btn-ghost flex-1 min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}>やっぱりやめる</button>
                <PulseButton isPrimary className="flex-1" onClick={save} disabled={saving}>
                    {saving ? '決めています…' : 'この名前でいく'}
                </PulseButton>
            </div>
        </ModalShell>
    );
};

const ModalShell: FC<{ title: string; onClose: () => void; children: ReactNode }> = ({ title, onClose, children }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
        <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-surface border-2 border-ink rounded-pop p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-display font-bold mb-4">{title}</h2>
            {children}
        </div>
    </div>
);
